import { AbruptMovementDetector } from "./AbruptMovementDetection";
import { AngleRFEstimator } from "./AngleRFEstimation";
import { AngleGyroEstimator } from "./AngleGyroEstimation";
import { DistanceEstimator } from "./DistanceEstimation";
import { HighSpeedDetector } from "./HighSpeedDetection";
import { LowLightDetector } from "./LowLightDetection";
import { OrientationEstimator } from "./OrientationEstimation";
import { PartCoverageEstimator } from "./PartCoverage";
import { Nudges } from "./NudgesFlag";

import * as constants from "../utils_new/Constants";
import * as utilFuncs from "../utils_new/UtilFuncs";

import _ from "underscore";

// --- KOTLIN-STYLE MATH UTILITIES ---
const MathUtils = {
  // Calculates the shortest distance on a 360-degree circle
  angularDistance: (a, b) => {
    if (a === -1 || b === -1) return Infinity;
    let diff = Math.abs(a - b) % 360;
    return diff > 180 ? 360 - diff : diff;
  },

  // Calculates a 2:1 weighted average favoring the Gyroscope
  avgAngle: (rf, gyro) => {
    const radRF = (rf * Math.PI) / 180;
    const radGyro = (gyro * Math.PI) / 180;

    let x = (2 * Math.cos(radGyro)) + Math.cos(radRF);
    let y = (2 * Math.sin(radGyro)) + Math.sin(radRF);

    let avg = Math.atan2(y, x) * (180 / Math.PI);
    return (avg + 360) % 360;
  }
};
// -----------------------------------

export class Guidance {
  constructor(messages, clientId, config) {
    this.actualFrameCounttrue = 0;
    this.actualFrameCountfalse = 0;
    this.actualFrameCall = 0;
    this.clientId = clientId;
    this.flagStencilVideo = false;
    config["blocks"].map((e) => {
      if (e["name"] === "Stencil Video" || e["name"] === "New Image App") {
        this.flagStencilVideo = true;
      }
    });
    this.frameCountPerRegion = {
      Front: 0,
      Rear: 0,
      "Side-Left": 0,
      "Side-Right": 0,
      "Front-Isometric-Left": 0,
      "Front-Isometric-Right": 0,
      "Rear-Isometric-Right": 0,
      "Rear-Isometric-Left": 0,
    };
    // Pause GuidanceModule
    this.pausePartCoveringAndFrameUpload = false;
    this.allFrameUpload = false;

    // VideoFreeze Variable
    this.videoFreezeStatus = false;

    // Warning Message List
    this.feedbacks = [];

    // Distance Variables
    this.distanceWarningCounter = 0;
    this.distanceWarningCounterThreshold = 20;
    this.distanceSlidingWindow = [];
    this.distanceWindowSize = 5;
    this.aggregatedDistance = false;
    this.zoomedInMessage = messages["zoomedIn"]; 
    this.farAwayMessage = messages["farAway"]; 

    // Detection Variables
    this.noBoundingBoxesCounter = 0;
    this.noBoundingBoxesCounterThreshold = 20;
    this.noDetectionMessage = messages["noDetection"]; 

    // --- KOTLIN SLIDING WINDOW STATE ---
    this.initWindowSize = 5;
    
    // Init Windows
    this.initAngleSlidingWindow = [];
    this.initOrientationSlidingWindow = [];
    
    // Continuous Windows
    this.continuousAngleSlidingWindow = [];
    this.continuousOrientationSlidingWindow = [];
    this.continuousRfAngle = -1;
    this.continuousRfOrientation = "Unclear";
    // -----------------------------------

    this.aggregatedAngle = constants.START_FLAG;
    this.aggregatedOrientation = constants.START_FLAG;

    // Speed Variables
    this.highSpeedCounter = 0;
    this.highSpeedCounterThreshold = 3;
    this.showhighSpeedMessageCounter = -1;
    this.showhighSpeedMessageCounterThreshold = 15;
    this.highSpeedMessage = messages["speedHigh"]; 

    // Abrupt Movement Variables
    this.abruptMovementCounter = 0;
    this.abruptMovementCounterThreshold = 20;
    this.showAbruptMovementWarningMessageCounter = -1;
    this.showAbruptMovementWarningMessageCounterThreshold = 15;
    this.abruptMovementMessage = messages["abruptMovement"]; 

    // Other Messages
    this.lowLightMessage = messages["lowLight"]; 
    this.initializingAngleMessage = messages["initializingAngle"]; 
    this.genericMessage = messages["generic"]; 

    // Angle Global Variables
    this.currentAngle = -1;
    // Region Global Variables
    this.currentRegion = constants.REGIONS.FRONT; 
    this.previousRegion = constants.REGIONS.FRONT; 
    this.currentBucket = "0_20"; 
    this.previousBucket = "0_20";
    this.revolutionRegionCurrent = "";

    // Frame Sampling per Region variables
    this.stage = 1;
    this.regionFrameDetails = [];
    this.regionFrameDetailsv2 = [];
    this.nMaxFramesCurrentRegion = 25;
    this.nMaxFramesUploadedPerRegion = 3;
    this.frameSelectionAngleStepThresh = 10;
    this.initialCoverageStatusPreviousRegion = constants.START_FLAG;

    //nudge variable
    this.nContinuousFrameForNudge = 3;
    this.nudgeWindow = [];
    this.currentNudge = { partToPoint: "", Nudge: "NA" };

    this.currentUploadedPartCount = {
      back_bumper: 0,
      back_glass: 0,
      dicky: 0,
      front_bumper: 0,
      front_glass: 0,
      hood: 0,
      left_running_board: 0,
      left_window_glass: 0,
      left_back_door: 0,
      left_front_door: 0,
      right_running_board: 0,
      right_window_glass: 0,
      right_back_door: 0,
      right_front_door: 0,
      left_fender: 0,
      right_fender: 0,
      left_qtr_panel: 0,
      right_qtr_panel: 0,
      left_front_bumper_isometric: 0,
      right_front_bumper_isometric: 0,
    };

    this.uploadedFramesAngleMap = {};
    for (let val of Object.values(constants.REGIONS))
      this.uploadedFramesAngleMap[val] = new Set();

    this.regionsCoveredMap = {};
    for (let val of Object.values(constants.REGIONS))
      this.regionsCoveredMap[val] =
        constants.REGION_COVERAGE_STATUS.NOT_COVERED;

    this.actualPartsCoveredMap = {};
    for (let val of Object.values(constants.REGIONS))
      this.actualPartsCoveredMap[val] = new Set();

    this.referencePartsCoveredMap = {};
    this.referencePartsCoveredMap[constants.REGIONS.FRONT] = new Set([
      "front_bumper",
      "hood",
      "front_glass",
    ]);
    if (["porto_seguros"].includes(clientId)) {
      this.referencePartsCoveredMap[constants.REGIONS.FRONT_ISOMETRIC_LEFT] =
        new Set(["left_fender"]);
      this.referencePartsCoveredMap[constants.REGIONS.FRONT_ISOMETRIC_RIGHT] =
        new Set(["right_fender"]);
    } else {
      this.referencePartsCoveredMap[constants.REGIONS.FRONT_ISOMETRIC_LEFT] =
        new Set(["left_fender", "left_front_bumper_isometric"]);
      this.referencePartsCoveredMap[constants.REGIONS.FRONT_ISOMETRIC_RIGHT] =
        new Set(["right_fender", "right_front_bumper_isometric"]);
    }
    this.referencePartsCoveredMap[constants.REGIONS.SIDE_LEFT] = new Set([
      "left_front_door",
      "left_back_door",
      "left_window_glass",
      "left_running_board",
    ]);
    this.referencePartsCoveredMap[constants.REGIONS.SIDE_RIGHT] = new Set([
      "right_front_door",
      "right_back_door",
      "right_window_glass",
      "right_running_board",
    ]);
    this.referencePartsCoveredMap[constants.REGIONS.REAR] = new Set([
      "back_bumper",
      "dicky",
      "back_glass",
    ]);
    this.referencePartsCoveredMap[constants.REGIONS.REAR_ISOMETRIC_LEFT] =
      new Set(["left_qtr_panel"]);
    this.referencePartsCoveredMap[constants.REGIONS.REAR_ISOMETRIC_RIGHT] =
      new Set(["right_qtr_panel"]);

    // Guidance Modules
    this.abruptMovementDetector = new AbruptMovementDetector();
    this.angleRFEstimator = new AngleRFEstimator();
    this.angleGyroEstimator = new AngleGyroEstimator();
    this.distanceEstimator = new DistanceEstimator();
    this.highSpeedDetector = new HighSpeedDetector();
    this.lowLightDetector = new LowLightDetector();
    this.orientationEstimator = new OrientationEstimator();
    this.partCoverageEstimator = new PartCoverageEstimator();
    this.nudges = new Nudges();
  }

  // --- NEW KOTLIN INITIALIZATION LOGIC ---
  initializeAngleFromBbox(boundingBoxDetections) {
    if (boundingBoxDetections.length === 0) return -1;

    let angleOfFrame = this.angleRFEstimator.getAngleOfFrame(boundingBoxDetections);
    let orientationOfFrame = this.orientationEstimator.getCarOrientation(boundingBoxDetections);

    this.initAngleSlidingWindow.push(angleOfFrame);
    this.initOrientationSlidingWindow.push(orientationOfFrame);

    if (this.initAngleSlidingWindow.length >= this.initWindowSize) {
      let angleMode = utilFuncs.getMode(this.initAngleSlidingWindow);
      let orientMode = utilFuncs.getMode(this.initOrientationSlidingWindow);

      this.initAngleSlidingWindow.length = 0;
      this.initOrientationSlidingWindow.length = 0;

      if (angleMode !== null && angleMode !== -1) {
        this.aggregatedAngle = angleMode;
        this.aggregatedOrientation = orientMode || "Unclear";

        // Validate: Unclear + angle in [20,160] -> reset and re-enter init
        if (this.aggregatedOrientation === "Unclear" && this.aggregatedAngle >= 20 && this.aggregatedAngle <= 160) {
          this.aggregatedAngle = -1;
          this.aggregatedOrientation = "Unclear";
          return -1;
        }

        // Compute final angle based on orientation
        if (this.aggregatedOrientation === "Left") return 360 - this.aggregatedAngle;
        if (this.aggregatedOrientation === "Right") return this.aggregatedAngle;
        return this.aggregatedAngle;
      }
    }
    return -1;
  }

  // --- NEW KOTLIN CONTINUOUS RF SMOOTHING LOGIC ---
  getContinuousRfAngle(boundingBoxDetections) {
    if (boundingBoxDetections.length === 0) {
      if (this.continuousRfAngle === -1) return -1;
      if (this.continuousRfOrientation === "Left") return 360 - this.continuousRfAngle;
      if (this.continuousRfOrientation === "Right") return this.continuousRfAngle;
      return this.continuousRfAngle;
    }

    let angleOfFrame = this.angleRFEstimator.getAngleOfFrame(boundingBoxDetections);
    let orientationOfFrame = this.orientationEstimator.getCarOrientation(boundingBoxDetections);

    this.continuousAngleSlidingWindow.push(angleOfFrame);
    this.continuousOrientationSlidingWindow.push(orientationOfFrame);

    if (this.continuousAngleSlidingWindow.length >= this.initWindowSize) {
      let angleMode = utilFuncs.getMode(this.continuousAngleSlidingWindow);
      let orientMode = utilFuncs.getMode(this.continuousOrientationSlidingWindow);

      this.continuousAngleSlidingWindow.length = 0;
      this.continuousOrientationSlidingWindow.length = 0;

      if (angleMode !== null && angleMode !== -1) {
        this.continuousRfAngle = angleMode;
        this.continuousRfOrientation = orientMode || "Unclear";

        if (this.continuousRfOrientation === "Unclear" && this.continuousRfAngle >= 20 && this.continuousRfAngle <= 160) {
          this.continuousRfAngle = -1;
          this.continuousRfOrientation = "Unclear";
          return -1;
        }
      }
    }

    if (this.continuousRfAngle === -1) return -1;
    if (this.continuousRfOrientation === "Left") return 360 - this.continuousRfAngle;
    if (this.continuousRfOrientation === "Right") return this.continuousRfAngle;
    return this.continuousRfAngle;
  }

  performDistanceCheck(boundingBoxDetections) {
    let distanceVal = this.distanceEstimator.checkDistance(
      boundingBoxDetections
    );
    this.distanceSlidingWindow.push(distanceVal);

    if (
      this.distanceSlidingWindow.length > 0 &&
      this.distanceSlidingWindow.length % this.distanceWindowSize === 0
    ) {
      this.aggregatedDistance = utilFuncs.getMode(this.distanceSlidingWindow);
      this.distanceSlidingWindow.length = 0;
    }

    if (this.aggregatedDistance) {
      this.distanceWarningCounter++;
      if (this.aggregatedDistance === 1)
        this.feedbacks.push(this.zoomedInMessage);
      if (this.aggregatedDistance === 2)
        this.feedbacks.push(this.farAwayMessage);
    } else this.distanceWarningCounter = 0;

    if (this.distanceWarningCounter >= this.distanceWarningCounterThreshold)
      this.videoFreezeStatus = true;
  }

  performDetectionCheck(boundingBoxDetections, processedFrame) {
    if (boundingBoxDetections.length === 0) {
      this.noBoundingBoxesCounter++;
      if (this.noBoundingBoxesCounter > 5) {
        if (this.lowLightDetector.checkLowLight2Trimmed(processedFrame))
          this.feedbacks.push(this.lowLightMessage);
        else this.feedbacks.push(this.noDetectionMessage);
      }
    } else this.noBoundingBoxesCounter = 0;

    if (this.noBoundingBoxesCounter >= this.noBoundingBoxesCounterThreshold)
      this.videoFreezeStatus = true;
  }

  setActualPartsCoveredMap(boundingBoxDetections, angle) {
    const allParts = [];

    for (const detection of boundingBoxDetections) {
      if (!allParts.includes(detection.label)) {
        allParts.push(detection.label);
      }
    }
    
    this.actualFrameCall = this.actualFrameCall + 1;
    let partsDetected = false;
    let partsCoveredWithConfidence =
      this.partCoverageEstimator.getPartsCoveredWithConfidence(
        boundingBoxDetections,
        angle,
        this.clientId
      );

    partsCoveredWithConfidence.forEach((part) => {
      for (let [region, partSet] of Object.entries(
        this.referencePartsCoveredMap
      )) {
        if (partSet.has(part)) {
          partsDetected = true;
          this.actualPartsCoveredMap[region].add(part);
        }
      }
    });
    partsDetected
      ? (this.actualFrameCounttrue = this.actualFrameCounttrue + 1)
      : (this.actualFrameCountfalse = this.actualFrameCountfalse + 1);
    return {
      partsDetected,
      actualFrameCall: this.actualFrameCall,
      actualFrameCounttrue: this.actualFrameCounttrue,
      actualFrameCountfalse: this.actualFrameCountfalse,
      partsCovered: partsCoveredWithConfidence,
      allPartsList: allParts,
    };
  }

  setRegionsCovered() {
    for (let [region, partSet] of Object.entries(this.actualPartsCoveredMap)) {
      if (partSet.size === this.referencePartsCoveredMap[region].size)
        this.regionsCoveredMap[region] =
          constants.REGION_COVERAGE_STATUS.FULLY_COVERED;
      else if (
        partSet.size > 0 &&
        partSet.size < this.referencePartsCoveredMap[region].size
      )
        this.regionsCoveredMap[region] =
          constants.REGION_COVERAGE_STATUS.PARTIALLY_COVERED;
    }
  }

  performSpeedCheck() {
    let userMovingTooFast = this.highSpeedDetector.checkHighSpeed(
      this.regionsCoveredMap
    );

    if (userMovingTooFast) {
      this.highSpeedCounter++;
      this.showhighSpeedMessageCounter = 0;
    }

    if (
      this.showhighSpeedMessageCounter >= 0 &&
      this.showhighSpeedMessageCounter <
        this.showAbruptMovementWarningMessageCounterThreshold
    ) {
      this.showhighSpeedMessageCounter++;
      this.feedbacks.push(this.highSpeedMessage);
    } else this.showhighSpeedMessageCounter = -1;

    if (this.highSpeedCounter >= this.highSpeedCounterThreshold)
      this.videoFreezeStatus = true;
  }

  performAbruptMovementCheck() {
    let abruptMovementDetected =
      this.abruptMovementDetector.checkAbruptMovement();

    if (abruptMovementDetected) {
      this.abruptMovementCounter++;
      this.showAbruptMovementWarningMessageCounter = 0;
    }

    if (
      this.showAbruptMovementWarningMessageCounter >= 0 &&
      this.showAbruptMovementWarningMessageCounter <
        this.showAbruptMovementWarningMessageCounterThreshold
    ) {
      this.showAbruptMovementWarningMessageCounter++;
      this.feedbacks.push(this.abruptMovementMessage);
    } else this.showAbruptMovementWarningMessageCounter = -1;

    if (this.abruptMovementCounter >= this.abruptMovementCounterThreshold)
      this.videoFreezeStatus = true;
  }

  checkAspectRatioOfFrontGlass(boundingBoxDetection) {
    let [wdth, ht] = boundingBoxDetection["bbox"].slice(2);
    if (ht <= wdth) return true;
    else return false;
  }

  checkAndChangeBumperToIsometric(boundingBoxDetection, angle) {
    if (
      (angle >= 40 && angle <= 60) ||
      (angle >= 120 && angle <= 140) ||
      (angle >= 220 && angle <= 240) ||
      (angle >= 300 && angle <= 320)
    )
      return boundingBoxDetection["label"] + "_isometric";
    else return boundingBoxDetection["label"];
  }

  refinePartDetections(boundingBoxDetections, angle) {
    let refinedBoundingBoxDetections = [];
    for (let i = 0; i < boundingBoxDetections.length; i++) {
      let boundingBoxDetection = boundingBoxDetections[i];

      if (boundingBoxDetection["label"] === "front_glass") {
        if (this.checkAspectRatioOfFrontGlass(boundingBoxDetection))
          refinedBoundingBoxDetections.push(boundingBoxDetection);
      } else if (boundingBoxDetection["label"].includes("bumper")) {
        boundingBoxDetection["label"] = this.checkAndChangeBumperToIsometric(
          boundingBoxDetection,
          angle
        );
        refinedBoundingBoxDetections.push(boundingBoxDetection);
      } else refinedBoundingBoxDetections.push(boundingBoxDetection);
    }
    return refinedBoundingBoxDetections;
  }

  getRegionFromAngle(angle) {
    if (angle <= 22.5 || angle >= 337.5) return constants.REGIONS.FRONT;
    else if (angle < 60) return constants.REGIONS.FRONT_ISOMETRIC_RIGHT;
    else if (angle <= 120) return constants.REGIONS.SIDE_RIGHT;
    else if (angle < 157.5) return constants.REGIONS.REAR_ISOMETRIC_RIGHT;
    else if (angle <= 202.5) return constants.REGIONS.REAR;
    else if (angle < 240) return constants.REGIONS.REAR_ISOMETRIC_LEFT;
    else if (angle <= 300) return constants.REGIONS.SIDE_LEFT;
    else if (angle < 337.5) return constants.REGIONS.FRONT_ISOMETRIC_LEFT;
  }

  getRegionFromAngleForStencil(angle) {
    if (angle <= 17.5 || angle >= 342.5) return constants.REGIONS.FRONT;
    else if (angle < 70) return constants.REGIONS.FRONT_ISOMETRIC_RIGHT;
    else if (angle <= 110) return constants.REGIONS.SIDE_RIGHT;
    else if (angle < 162.5) return constants.REGIONS.REAR_ISOMETRIC_RIGHT;
    else if (angle <= 197.5) return constants.REGIONS.REAR;
    else if (angle < 250) return constants.REGIONS.REAR_ISOMETRIC_LEFT;
    else if (angle <= 290) return constants.REGIONS.SIDE_LEFT;
    else if (angle < 342.5) return constants.REGIONS.FRONT_ISOMETRIC_LEFT;
  }

  getRegionFromAngleForRevolution(angle) {
    if (angle <= 22.5 || angle >= 337.5) return constants.REGIONS.FRONT;
    else if (angle < 75) return constants.REGIONS.FRONT_ISOMETRIC_RIGHT;
    else if (angle <= 105) return constants.REGIONS.SIDE_RIGHT;
    else if (angle < 157.5) return constants.REGIONS.REAR_ISOMETRIC_RIGHT;
    else if (angle <= 202.5) return constants.REGIONS.REAR;
    else if (angle < 255) return constants.REGIONS.REAR_ISOMETRIC_LEFT;
    else if (angle <= 285) return constants.REGIONS.SIDE_LEFT;
    else if (angle < 337.5) return constants.REGIONS.FRONT_ISOMETRIC_LEFT;
  }

  getAngleBucket(angle) {
    if (angle <= 22.5) return "0_20";
    else if (angle <= 42.5) return "20_40";
    else if (angle <= 62.5) return "40_60";
    else if (angle <= 82.5) return "60_80";
    else if (angle <= 102.5) return "80_100";
    else if (angle <= 122.5) return "100_120";
    else if (angle <= 142.5) return "120_140";
    else if (angle <= 162.5) return "140_160";
    else if (angle <= 182.5) return "160_180";
    else if (angle <= 202.5) return "180_200";
    else if (angle <= 222.5) return "200_220";
    else if (angle <= 242.5) return "220_240";
    else if (angle <= 262.5) return "240_260";
    else if (angle <= 282.5) return "260_280";
    else if (angle <= 302.5) return "280_300";
    else if (angle <= 322.5) return "300_320";
    else if (angle <= 342.5) return "320_340";
    else if (angle <= 360) return "340_360";
  }

  getNudgeFlag(partCoveredMap, currentRegion, boundingBoxDetections) {
    let nudge_data = this.nudges.getNudgeFromSingleFrameDetection(
      partCoveredMap,
      currentRegion,
      boundingBoxDetections
    );
    let nudge = nudge_data["Nudge"];
    let partToPoint = nudge_data["partToPoint"];

    if (this.nudgeWindow.includes(nudge) || !this.nudgeWindow.length) {
      this.nudgeWindow.push(nudge);
    } else {
      this.nudgeWindow = [];
      this.nudgeWindow.push(nudge);
    }

    if (
      this.nudgeWindow.length &&
      this.nudgeWindow.length % this.nContinuousFrameForNudge === 0
    ) {
      return { partToPoint: partToPoint, Nudge: this.nudgeWindow[0] };
    }

    return { partToPoint: "", Nudge: "NA" };
  }

  // --- UPDATED MAIN LOGIC LOOP ---
  performChecksAndGetWarningMessage(
    boundingBoxDetections,
    processedFrame,
    URL
  ) {
    this.allowImgURL = {
      partsDetected: false,
      actualFrameCall: this.actualFrameCall,
      actualFrameCounttrue: this.actualFrameCounttrue,
      actualFrameCountfalse: this.actualFrameCountfalse,
      partsCovered: [],
      allPartsList: [],
    };
    this.imgURL = URL;
    this.feedbacks.length = 0;
    this.videoFreezeStatus = false;

    this.performDistanceCheck(boundingBoxDetections);
    this.performDetectionCheck(boundingBoxDetections, processedFrame);
    this.performAbruptMovementCheck();

    let gyroAngle = -1;
    let rawGyroAngle = -1;
    let rfAngle = -1;

    // 1. Initialization Phase (Wait for 5 solid frames)
    if (this.aggregatedAngle === constants.START_FLAG || this.aggregatedAngle === -1) {
      this.currentAngle = this.initializeAngleFromBbox(boundingBoxDetections);
      rawGyroAngle = -1;
      
      if (this.currentAngle === -1) {
         this.feedbacks.push(this.initializingAngleMessage);
      } else {
         this.feedbacks.push(this.genericMessage);
      }
    } 
    // 2. Continuous Tracking Phase
    else {
      this.feedbacks.push(this.genericMessage);
      
      // Get the continuous, smoothed RF angle
      rfAngle = this.getContinuousRfAngle(boundingBoxDetections);
      
      // Get the Gyro Angle using the locked-in initialization data
      gyroAngle = this.angleGyroEstimator.getAngleOfFrame(
        this.aggregatedAngle,
        this.aggregatedOrientation
      );
      rawGyroAngle = gyroAngle;

      // 3. Vector Math Blending (Gyro heavily weighted)
      if (
        this.flagStencilVideo && 
        gyroAngle !== -1 &&
        rfAngle !== -1 &&
        MathUtils.angularDistance(gyroAngle, rfAngle) < 41 
      ) {
        let avg = MathUtils.avgAngle(rfAngle, gyroAngle);
        this.currentAngle = Math.round(avg / 5) * 5; 
      } else {
        this.currentAngle = gyroAngle;
      }
    }

    if (this.currentAngle !== -1) {
      this.currentRegion = this.flagStencilVideo
        ? this.getRegionFromAngleForStencil(this.currentAngle)
        : this.getRegionFromAngle(this.currentAngle);
      this.revolutionRegionCurrent = this.getRegionFromAngleForRevolution(
        this.currentAngle
      );
    }

    if (this.pausePartCoveringAndFrameUpload)
      return [
        this.currentAngle,
        this.currentRegion,
        this.regionsCoveredMap,
        this.feedbacks,
        this.videoFreezeStatus,
        this.currentNudge,
        this.imgURL,
        this.allowImgURL,
        this.frameCountPerRegion,
        this.revolutionRegionCurrent,
        rawGyroAngle, 
        rfAngle,
      ];

    if (this.currentAngle !== -1) {
      boundingBoxDetections = this.refinePartDetections(
        boundingBoxDetections,
        this.currentAngle
      );
      this.allowImgURL = this.setActualPartsCoveredMap(
        boundingBoxDetections,
        this.currentAngle
      );
    }

    this.setRegionsCovered();
    this.performSpeedCheck();

    if (this.currentAngle !== -1) {
      this.currentNudge = this.getNudgeFlag(
        this.actualPartsCoveredMap,
        this.currentRegion,
        boundingBoxDetections
      );
    }

    return [
      this.currentAngle,
      this.currentRegion,
      this.regionsCoveredMap,
      this.feedbacks,
      this.videoFreezeStatus,
      this.currentNudge,
      this.allowImgURL.partsDetected ? this.imgURL : false,
      this.allowImgURL,
      this.frameCountPerRegion,
      this.revolutionRegionCurrent,
      rawGyroAngle, 
      rfAngle,
    ];
  }

  sortFramesOfRegion(regionFrameDetails) {
    if (regionFrameDetails.length <= this.nMaxFramesUploadedPerRegion)
      return regionFrameDetails;

    const sortedRegionFrameDetails = _.sortBy(
      regionFrameDetails,
      (frame_detail) => frame_detail["detections"].length
    );
    const uniqueAngleSortedRegionFrameDetails = _.uniq(
      sortedRegionFrameDetails,
      (frame_detail) => frame_detail["angle"]
    );
    if (
      uniqueAngleSortedRegionFrameDetails.length <=
      this.nMaxFramesUploadedPerRegion
    )
      return uniqueAngleSortedRegionFrameDetails;

    const uniqueAngleMultipleSortedRegionFrameDetails =
      uniqueAngleSortedRegionFrameDetails.filter(
        (frame_detail) =>
          !(frame_detail["angle"] % this.frameSelectionAngleStepThresh)
      );
    return uniqueAngleMultipleSortedRegionFrameDetails.slice(
      0,
      this.nMaxFramesUploadedPerRegion
    );
  }

  sortFramesOfRegionV2(regionFrameDetails) {
    if (regionFrameDetails.length === 0) {
      return regionFrameDetails;
    }

    let maxNumberOfFrameToUpload;
    let uploadFrameFlag;

    const partListForCurrentRegion = [
      ...this.referencePartsCoveredMap[regionFrameDetails[0]["region"]],
    ];

    if (constants.DIGILERA_CLIET_LIST.includes(this.clientId)) {
      maxNumberOfFrameToUpload =
        constants.MAX_NUMBER_OF_FRAME_PER_REGION_SHORT[
          regionFrameDetails[0]["region"]
        ];
      uploadFrameFlag = partListForCurrentRegion.some(
        (part) => this.currentUploadedPartCount[part] < 2
      );
    } else {
      maxNumberOfFrameToUpload =
        constants.MAX_NUMBER_OF_FRAME_PER_REGION[
          regionFrameDetails[0]["region"]
        ];
      uploadFrameFlag = partListForCurrentRegion.some(
        (part) => this.currentUploadedPartCount[part] < 3
      );
    }

    let framesToPushV2 = [];

    if (!uploadFrameFlag) {
      return framesToPushV2;
    }
    let sortedRegionFrameDetails = _.sortBy(
      regionFrameDetails,
      (frame_detail) =>
        frame_detail?.detections ? frame_detail.detections.length : 0
    ).reverse();

    sortedRegionFrameDetails = sortedRegionFrameDetails.filter(
      (regionFrameDetail) => {
        if (
          typeof regionFrameDetail === "undefined" ||
          (!Object.keys(regionFrameDetail).length > 0 &&
            !regionFrameDetail?.detections.length > 0)
        ) {
          return false;
        }
        let uploadFrameFlagInner = false;
        const coveredPartsInFrame =
          this.partCoverageEstimator.getPartsCoveredWithConfidence(
            regionFrameDetail["detections"],
            regionFrameDetail["angle"],
            this.clientId
          );
        coveredPartsInFrame.forEach((part) => {
          if (
            partListForCurrentRegion.includes(part) &&
            this.currentUploadedPartCount[part] < 3
          ) {
            uploadFrameFlagInner = true;

            this.currentUploadedPartCount[part] =
              this.currentUploadedPartCount[part] + 1;
          }
        });

        if (
          framesToPushV2.length < maxNumberOfFrameToUpload &&
          uploadFrameFlagInner
        ) {
          framesToPushV2.push(regionFrameDetail);
          return false; 
        }
        return true;
      }
    );

    if (framesToPushV2.length < maxNumberOfFrameToUpload) {
      const perfectAngleCurrentRegion =
        constants.PERFECT_ANGLE_PER_REGION[regionFrameDetails[0]["region"]];

      const perfectAngleFrame = sortedRegionFrameDetails.find(
        (regionFrameDetail) =>
          regionFrameDetail["angle"] === perfectAngleCurrentRegion
      );

      if (perfectAngleFrame) {
        framesToPushV2.push(perfectAngleFrame);
        sortedRegionFrameDetails = sortedRegionFrameDetails.filter(
          (frame) => frame !== perfectAngleFrame
        );
      }
    }

    let remainingFrameCount = maxNumberOfFrameToUpload - framesToPushV2.length;

    if (remainingFrameCount > 0 && sortedRegionFrameDetails.length > 0) {
      let filteredFrames = sortedRegionFrameDetails;

      if (sortedRegionFrameDetails.length > remainingFrameCount) {
        filteredFrames = _.uniq(
          sortedRegionFrameDetails,
          (frame_detail) => frame_detail["angle"]
        );
      }

      if (filteredFrames.length > remainingFrameCount) {
        filteredFrames = filteredFrames.filter(
          (frame_detail) =>
            frame_detail["angle"] % this.frameSelectionAngleStepThresh === 0
        );
      }

      if (filteredFrames.length > remainingFrameCount) {
        const indexListofframe = utilFuncs.equidistantIndex(
          remainingFrameCount,
          filteredFrames.length
        );
        indexListofframe.forEach((index) => {
          framesToPushV2.push(filteredFrames[index]);
        });
      } else {
        framesToPushV2.push(...filteredFrames);
      }
    }
    this.frameCountPerRegion[regionFrameDetails[0]["region"]] =
      this.frameCountPerRegion[regionFrameDetails[0]["region"]] +
      framesToPushV2.length;
    return framesToPushV2;
  }

  sortFramesOfRegionStencil(regionFrameDetails) {
    if (regionFrameDetails.length === 0) {
      return regionFrameDetails;
    }

    let maxNumberOfFrameToUpload;
    let uploadFrameFlag;

    const partListForCurrentRegion = [
      ...this.referencePartsCoveredMap[regionFrameDetails[0]["region"]],
    ];


    maxNumberOfFrameToUpload = 1;
    uploadFrameFlag = partListForCurrentRegion.some(
        (part) => this.currentUploadedPartCount[part] < 3
      );

    let framesToPushV2 = [];

    if (!uploadFrameFlag) {
      return framesToPushV2;
    }
    let sortedRegionFrameDetails = _.sortBy(
      regionFrameDetails,
      (frame_detail) =>
        frame_detail?.detections ? frame_detail.detections.length : 0
    ).reverse();

    sortedRegionFrameDetails = sortedRegionFrameDetails.filter(
      (regionFrameDetail) => {
        if (
          typeof regionFrameDetail === "undefined" ||
          (!Object.keys(regionFrameDetail).length > 0 &&
            !regionFrameDetail?.detections.length > 0)
        ) {
          return false;
        }
        let uploadFrameFlagInner = false;
        const coveredPartsInFrame =
          this.partCoverageEstimator.getPartsCoveredWithConfidence(
            regionFrameDetail["detections"],
            regionFrameDetail["angle"],
            this.clientId
          );
        coveredPartsInFrame.forEach((part) => {
          if (
            partListForCurrentRegion.includes(part) &&
            this.currentUploadedPartCount[part] < 3
          ) {
            uploadFrameFlagInner = true;

            this.currentUploadedPartCount[part] =
              this.currentUploadedPartCount[part] + 1;
          }
        });

        if (
          framesToPushV2.length < maxNumberOfFrameToUpload &&
          uploadFrameFlagInner
        ) {
          framesToPushV2.push(regionFrameDetail);
          return false; 
        }
        return true;
      }
    );

    if (framesToPushV2.length < maxNumberOfFrameToUpload) {
      const perfectAngleCurrentRegion =
        constants.PERFECT_ANGLE_PER_REGION[regionFrameDetails[0]["region"]];

      const perfectAngleFrame = sortedRegionFrameDetails.find(
        (regionFrameDetail) =>
          regionFrameDetail["angle"] === perfectAngleCurrentRegion
      );

      if (perfectAngleFrame) {
        framesToPushV2.push(perfectAngleFrame);
        sortedRegionFrameDetails = sortedRegionFrameDetails.filter(
          (frame) => frame !== perfectAngleFrame
        );
      }
    }

    let remainingFrameCount = maxNumberOfFrameToUpload - framesToPushV2.length;

    if (remainingFrameCount > 0 && sortedRegionFrameDetails.length > 0) {
      let filteredFrames = sortedRegionFrameDetails;

      if (sortedRegionFrameDetails.length > remainingFrameCount) {
        filteredFrames = _.uniq(
          sortedRegionFrameDetails,
          (frame_detail) => frame_detail["angle"]
        );
      }

      if (filteredFrames.length > remainingFrameCount) {
        filteredFrames = filteredFrames.filter(
          (frame_detail) =>
            frame_detail["angle"] % this.frameSelectionAngleStepThresh === 0
        );
      }

      if (filteredFrames.length > remainingFrameCount) {
        const indexListofframe = utilFuncs.equidistantIndex(
          remainingFrameCount,
          filteredFrames.length
        );
        indexListofframe.forEach((index) => {
          framesToPushV2.push(filteredFrames[index]);
        });
      } else {
        framesToPushV2.push(...filteredFrames);
      }
    }
    this.frameCountPerRegion[regionFrameDetails[0]["region"]] =
      this.frameCountPerRegion[regionFrameDetails[0]["region"]] +
      framesToPushV2.length;
    return framesToPushV2;
  }

  getFramesToPush(frameEncoded, boundingBoxDetections, stopRecordingML, captureTimestamp) {
    let framesToPush = [];

    if (
      this.previousRegion !== this.currentRegion ||
      this.checkIfAllRegionsCovered() ||
      stopRecordingML
    ) {
      let framesToPush1 = this.sortFramesOfRegion(this.regionFrameDetails);
      let framesToPush2 = this.sortFramesOfRegionV2(this.regionFrameDetailsv2);
      framesToPush.push(framesToPush1);
      framesToPush.push(framesToPush2);
      if (stopRecordingML && framesToPush2.length === 0) {
        this.allFrameUpload = true;
        this.pausePartCoveringAndFrameUpload = true;
      }
      
      if (stopRecordingML) {
        return {
          framesToPush: framesToPush,
          currentUploadedPartCount: this.currentUploadedPartCount,
          allFrameUpload: this.allFrameUpload,
        };
      }

      this.previousRegion = this.currentRegion;
      this.initialCoverageStatusPreviousRegion =
        this.regionsCoveredMap[this.previousRegion];
      this.regionFrameDetails = [];
      this.regionFrameDetailsv2 = [];
    }

    let notProbablePartDetectedFlag = false;

    if (boundingBoxDetections.length === 0) {
      notProbablePartDetectedFlag = true;
    }
    for (let i = 0; i < boundingBoxDetections.length; i++) {
      notProbablePartDetectedFlag = true;
      let detection_classLabel = boundingBoxDetections[i]["label"];
      if (
        this.referencePartsCoveredMap[this.currentRegion].has(
          detection_classLabel
        ) ||
        this.referencePartsCoveredMap[this.currentRegion].has(
          "left_" + detection_classLabel
        ) ||
        this.referencePartsCoveredMap[this.currentRegion].has(
          "right_" + detection_classLabel
        )
      ) {
        notProbablePartDetectedFlag = false;
        break;
      }
    }
    if (!notProbablePartDetectedFlag) {
      let frameObj = {
        angle: this.currentAngle,
        region: this.currentRegion,
        frameEncoded: frameEncoded,
        detections: boundingBoxDetections,
        regionsMap: this.regionsCoveredMap,
        feedbacks: this.feedbacks,
      };

      let allowedClients = ["client_1630", "client_1630_uat", "client_1630_dev", "client_1630_prod", "client_1630_stagging", "anuj_garg"];
      if (allowedClients.includes(this.clientId) && captureTimestamp) {
        frameObj.captureTimestamp = captureTimestamp;
      }

      this.regionFrameDetails.push(frameObj);
      this.regionFrameDetailsv2 = [...this.regionFrameDetails];
    }

    if (this.regionFrameDetails.length > this.nMaxFramesCurrentRegion)
      this.regionFrameDetails = this.regionFrameDetails.slice(
        Math.floor(this.nMaxFramesCurrentRegion / 2)
      );

    if (this.regionFrameDetailsv2.length > this.nMaxFramesCurrentRegion) {
      let indexListofframe = utilFuncs.equidistantIndex(
        this.nMaxFramesCurrentRegion,
        this.regionFrameDetailsv2.length
      );
      this.regionFrameDetailsv2 = indexListofframe.map(
        (frame) => this.regionFrameDetailsv2[frame]
      );
    }

    return {
      framesToPush: framesToPush,
      currentUploadedPartCount: this.currentUploadedPartCount,
      allFrameUpload: this.allFrameUpload,
    };
  }

  getFramesToPushStencil(frameEncoded, boundingBoxDetections, stopRecordingML, captureTimestamp) {
    let framesToPush = [];

    this.currentBucket = this.getAngleBucket(this.currentRegion)
    this.previousBucket = "0_20";
    if (
      this.currentBucket !== this.previousBucket ||
      this.checkIfAllRegionsCovered() ||
      stopRecordingML
    ) {
      let framesToPush1 = this.sortFramesOfRegionStencil(this.regionFrameDetailsv2);
      framesToPush.push(framesToPush1);
      if (stopRecordingML && framesToPush1.length === 0) {
        this.allFrameUpload = true;
        this.pausePartCoveringAndFrameUpload = true;
      }
      
      if (stopRecordingML) {
        return {
          framesToPush: framesToPush,
          currentUploadedPartCount: this.currentUploadedPartCount,
          allFrameUpload: this.allFrameUpload,
        };
      }

      this.previousBucket = this.currentBucket;
      this.initialCoverageStatusPreviousRegion =
        this.regionsCoveredMap[this.previousRegion];
      this.regionFrameDetailsv2 = [];
    }

    let notProbablePartDetectedFlag = false;

    if (boundingBoxDetections.length === 0) {
      notProbablePartDetectedFlag = true;
    }
    for (let i = 0; i < boundingBoxDetections.length; i++) {
      notProbablePartDetectedFlag = true;
      let detection_classLabel = boundingBoxDetections[i]["label"];
      if (
        this.referencePartsCoveredMap[this.currentRegion].has(
          detection_classLabel
        ) ||
        this.referencePartsCoveredMap[this.currentRegion].has(
          "left_" + detection_classLabel
        ) ||
        this.referencePartsCoveredMap[this.currentRegion].has(
          "right_" + detection_classLabel
        )
      ) {
        notProbablePartDetectedFlag = false;
        break;
      }
    }
    if (!notProbablePartDetectedFlag) {
      let frameObj = {
        angle: this.currentAngle,
        region: this.currentRegion,
        frameEncoded: frameEncoded,
        detections: boundingBoxDetections,
        regionsMap: this.regionsCoveredMap,
        feedbacks: this.feedbacks,
      };

      let allowedClients = ["client_1630", "client_1630_uat", "client_1630_dev", "client_1630_prod", "client_1630_stagging", "anuj_garg"];
      if (allowedClients.includes(this.clientId) && captureTimestamp) {
        frameObj.captureTimestamp = captureTimestamp;
      }

      this.regionFrameDetailsv2.push(frameObj);
    }

    if (this.regionFrameDetailsv2.length > this.nMaxFramesCurrentRegion) {
      let indexListofframe = utilFuncs.equidistantIndex(
        this.nMaxFramesCurrentRegion,
        this.regionFrameDetailsv2.length
      );
      this.regionFrameDetailsv2 = indexListofframe.map(
        (frame) => this.regionFrameDetailsv2[frame]
      );
    }

    return {
      framesToPush: framesToPush,
      currentUploadedPartCount: this.currentUploadedPartCount,
      allFrameUpload: this.allFrameUpload,
    };
  }

  checkIfAllRegionsCovered() {
    return Object.values(this.regionsCoveredMap).every(
      (val) => val === constants.REGION_COVERAGE_STATUS.FULLY_COVERED
    );
  }
}